import { useEffect, useMemo, useState, type ReactNode } from 'react';
import { Badge } from 'antd';
import type { TabsProps } from 'antd';
import { useTranslation } from 'react-i18next';
import type {
  MaintenanceContractIssue,
  MaintenancePartnershipValidity,
  MaintenanceSlaDraft,
  MaintenanceSlaDraftLevel,
  MaintenanceSlaScopeOptions,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import {
  MAINTENANCE_SLA_ON_SITE_LEVELS,
  MAINTENANCE_SLA_REMOTE_LEVELS,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/types';
import type { MaintenanceSlaContractState } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/hooks/useMaintenanceSlaContract';
import { MaintenanceSlaPartnershipValidity } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaPartnershipValidity/MaintenanceSlaPartnershipValidity';
import { MaintenanceSlaHeaderIdentityFields } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaHeaderIdentityFields/MaintenanceSlaHeaderIdentityFields';
import { MaintenanceSlaHeaderScheduleFields } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaHeaderScheduleFields/MaintenanceSlaHeaderScheduleFields';
import { MaintenanceSlaLevelCard } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaLevelCard/MaintenanceSlaLevelCard';
import { MaintenanceSlaScopeRulesEditor } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaScopeRulesEditor/MaintenanceSlaScopeRulesEditor';
import { MaintenanceSlaHeaderInterventionModeFields } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaHeaderInterventionModeFields/MaintenanceSlaHeaderInterventionModeFields';
import {
  countMaintenanceFormTabIssues,
  firstMaintenanceFormTabWithIssues,
  maintenanceFormTabsForInterventionMode,
  type MaintenanceFormTabKey,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/utils/maintenance-form-tab.utils';
import {
  formTabPanelStyle,
  formTabsRootStyle,
} from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaContractForm/MaintenanceSlaContractForm.style';
import { FormSectionHeader } from '~/app/landing/sla/oems/components/MaintenanceSlaContract/components/MaintenanceSlaContractForm/MaintenanceSlaContractFormSections';

type Args = {
  partnership: MaintenancePartnershipValidity;
  draft: MaintenanceSlaDraft | null;
  state: MaintenanceSlaContractState;
  scopeOptions: MaintenanceSlaScopeOptions;
  scopeOptionsLoading: boolean;
  showValidation: boolean;
  allIssues: readonly MaintenanceContractIssue[];
};

type MaintenanceFormTab = NonNullable<TabsProps['items']>[number];

function tabLabel(text: string, issueCount: number): ReactNode {
  if (issueCount <= 0) {
    return text;
  }
  return (
    <span style={{ display: 'inline-flex', alignItems: 'center', gap: 8 }}>
      {text}
      <Badge count={issueCount} size="small" />
    </span>
  );
}

function levelsPanel(
  levels: MaintenanceSlaDraftLevel[],
  state: MaintenanceSlaContractState,
  currency: string,
) {
  return (
    <div style={formTabPanelStyle}>
      {levels.map((level, index) => (
        <MaintenanceSlaLevelCard
          key={level.code}
          level={level}
          issues={state.visibleIssues}
          onChange={(next) => state.updateLevel(level.code, next)}
          defaultOpen={index === 0}
          currency={currency}
        />
      ))}
    </div>
  );
}

export function useMaintenanceSlaContractFormTabs({
  partnership,
  draft,
  state,
  scopeOptions,
  scopeOptionsLoading,
  showValidation,
  allIssues,
}: Args): TabsProps {
  const { t } = useTranslation('sla');
  const interventionMode = draft?.interventionMode ?? null;
  const visibleTabKeys = useMemo(
    () => maintenanceFormTabsForInterventionMode(interventionMode),
    [interventionMode],
  );
  const [activeKey, setActiveKey] = useState<MaintenanceFormTabKey>('general');

  useEffect(() => {
    if (!visibleTabKeys.includes(activeKey)) {
      setActiveKey(visibleTabKeys[0] ?? 'general');
    }
  }, [activeKey, visibleTabKeys]);

  useEffect(() => {
    if (!showValidation || !draft) {
      return;
    }
    const first = firstMaintenanceFormTabWithIssues(
      allIssues,
      draft.interventionMode,
    );
    if (first) {
      setActiveKey(first);
    }
  }, [allIssues, draft, showValidation]);

  const issuesForBadges = showValidation ? allIssues : state.visibleIssues;

  const items = useMemo((): TabsProps['items'] => {
    if (!draft) {
      return [];
    }

    const onSite = draft.levels.filter((level) =>
      (MAINTENANCE_SLA_ON_SITE_LEVELS as readonly string[]).includes(
        level.code,
      ),
    );
    const remote = draft.levels.filter((level) =>
      (MAINTENANCE_SLA_REMOTE_LEVELS as readonly string[]).includes(level.code),
    );

    const general: MaintenanceFormTab = {
      key: 'general',
      label: tabLabel(
        t('oems.maintenance.formTabs.general'),
        countMaintenanceFormTabIssues(issuesForBadges, 'general'),
      ),
      children: (
        <div style={formTabPanelStyle}>
          <FormSectionHeader
            color="#52c41a"
            label={t('oems.maintenance.sections.partnership')}
            first
          />
          <MaintenanceSlaPartnershipValidity partnership={partnership} />
          <FormSectionHeader
            color="#1677ff"
            label={t('oems.maintenance.sections.operation')}
          />
          <MaintenanceSlaHeaderIdentityFields
            draft={draft}
            issues={state.visibleIssues}
            onChange={state.updateDraft}
          />
          <div style={{ marginTop: 12 }}>
            <MaintenanceSlaHeaderInterventionModeFields
              draft={draft}
              onChange={state.updateDraft}
            />
          </div>
          <FormSectionHeader
            color="#fa8c16"
            label={t('oems.maintenance.sections.schedule')}
          />
          <MaintenanceSlaHeaderScheduleFields
            draft={draft}
            issues={state.visibleIssues}
            onChange={state.updateDraft}
          />
        </div>
      ),
    };

    const onSiteTab: MaintenanceFormTab = {
      key: 'onSite',
      label: tabLabel(
        t('oems.maintenance.formTabs.onSite'),
        countMaintenanceFormTabIssues(issuesForBadges, 'onSite'),
      ),
      children: levelsPanel(onSite, state, draft.priceCurrency),
    };

    const remoteTab: MaintenanceFormTab = {
      key: 'remote',
      label: tabLabel(
        t('oems.maintenance.formTabs.remote'),
        countMaintenanceFormTabIssues(issuesForBadges, 'remote'),
      ),
      children: levelsPanel(remote, state, draft.priceCurrency),
    };

    const scopeTab: MaintenanceFormTab = {
      key: 'scope',
      label: tabLabel(
        t('oems.maintenance.formTabs.scope'),
        countMaintenanceFormTabIssues(issuesForBadges, 'scope'),
      ),
      children: (
        <div style={formTabPanelStyle}>
          <MaintenanceSlaScopeRulesEditor
            rules={draft.scopeRules}
            issues={state.visibleIssues}
            scopeOptions={scopeOptions}
            scopeAssetHierarchy={scopeOptions.assetHierarchy ?? []}
            scopeOptionsLoading={scopeOptionsLoading}
            interventionMode={draft.interventionMode}
            onAdd={state.addScopeRule}
            onChange={state.updateScopeRule}
            onRemove={state.removeScopeRule}
          />
        </div>
      ),
    };

    return visibleTabKeys
      .map((key) => {
        switch (key) {
          case 'general':
            return general;
          case 'onSite':
            return onSiteTab;
          case 'remote':
            return remoteTab;
          case 'scope':
            return scopeTab;
          default:
            return null;
        }
      })
      .filter((item): item is NonNullable<typeof item> => item != null);
  }, [
    draft,
    issuesForBadges,
    partnership,
    scopeOptions,
    scopeOptionsLoading,
    state,
    t,
    visibleTabKeys,
  ]);

  return {
    items,
    activeKey,
    onChange: (key) => setActiveKey(key as MaintenanceFormTabKey),
    destroyInactiveTabPane: false,
    style: formTabsRootStyle,
  };
}
